import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { apiClient } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { AdminNav } from "./AdminPage";

/**
 * Cycle 15 STORY-1502 — Portfolio Health Dashboard.
 *
 * Admin-only page показывает grid 13 продуктов:
 * - status: 🟢 OK / 🟡 SLOW / 🔴 FAIL / ⚪ UNKNOWN
 * - per-product: name, http_code, ttfb, total, last deploy
 * - overall: OK / DEGRADED / DOWN
 *
 * Polls /api/portfolio/status каждые 30 sec.
 */

type ProductStatus = {
  slug: string;
  name: string;
  status: "OK" | "SLOW" | "FAIL" | "UNKNOWN";
  http_code: number | null;
  ttfb_ms: number | null;
  total_ms: number | null;
  last_check_at: string | null;
  last_deploy_at: string | null;
  last_deploy_sha: string | null;
  last_deploy_subject: string | null;
};

type PortfolioStatusResponse = {
  timestamp: string;
  overall_status: "OK" | "DEGRADED" | "DOWN";
  products: ProductStatus[];
  summary: { ok: number; slow: number; fail: number; unknown: number };
};

const statusConfig: Record<ProductStatus["status"], { icon: string; color: string; bg: string; label: string }> = {
  OK: { icon: "🟢", color: "#0a7a4a", bg: "#e6f7ee", label: "Работает" },
  SLOW: { icon: "🟡", color: "#a07000", bg: "#fff7d6", label: "Замедленно" },
  FAIL: { icon: "🔴", color: "#a01c2a", bg: "#fde8eb", label: "Недоступен" },
  UNKNOWN: { icon: "⚪", color: "#5a6378", bg: "#eef0f5", label: "Нет данных" },
};

const overallConfig: Record<PortfolioStatusResponse["overall_status"], { color: string; bg: string; text: string }> = {
  OK: { color: "#0a7a4a", bg: "#d6f5e3", text: "✓ Все системы работают" },
  DEGRADED: { color: "#a07000", bg: "#fff0c2", text: "⚠ Частичная деградация" },
  DOWN: { color: "#a01c2a", bg: "#fed5da", text: "✗ Критические проблемы" },
};

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s назад`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m назад`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h назад`;
  return `${Math.floor(seconds / 86400)}d назад`;
}

export function HealthDashboardPage(): ReactElement {
  const { hasAdminJwt } = useAuth();

  const { data, isLoading, error, refetch } = useQuery<PortfolioStatusResponse>({
    queryKey: ["portfolio-status"],
    queryFn: async () => {
      const response = await apiClient.get<PortfolioStatusResponse>("/api/portfolio/status");
      return response.data;
    },
    refetchInterval: 30_000, // 30 sec poll
    enabled: hasAdminJwt,
  });

  if (!hasAdminJwt) {
    return (
      <section style={{ padding: 32, maxWidth: 600, margin: "0 auto" }}>
        <h1>Portfolio Health</h1>
        <p style={{ color: "#5a6378" }}>
          Требуется admin authentication. <Link to="/admin">Войти как admin</Link>
        </p>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section style={{ padding: 32 }}>
        <h1>Portfolio Health</h1>
        <p>Загружаем статус...</p>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section style={{ padding: 32, maxWidth: 800, margin: "0 auto" }}>
        <h1>Portfolio Health</h1>
        <div style={{ padding: 16, background: "#fed5da", borderRadius: 8, color: "#a01c2a" }}>
          Не удалось загрузить статус. <button onClick={() => refetch()}>Повторить</button>
        </div>
      </section>
    );
  }

  const overall = overallConfig[data.overall_status];

  return (
    <section style={{ maxWidth: 1200, margin: "0 auto" }}>
      <AdminNav />
      <header style={{ marginBottom: 24, padding: "8px 24px 0" }}>
        <h1 style={{ margin: "0 0 8px" }}>Portfolio Health Dashboard</h1>
        <p style={{ margin: 0, color: "#5a6378", fontSize: 14 }}>
          Cycle 15 — real-time status 13 продуктов IT-Navigate · обновляется каждые 30 sec
        </p>
      </header>

      <div style={{ padding: "0 24px 32px" }}>
      <div
        role="status"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          background: overall.bg,
          color: overall.color,
          borderRadius: 12,
          marginBottom: 32,
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        <span>{overall.text}</span>
        <span style={{ fontSize: 14, fontWeight: 500 }}>
          OK {data.summary.ok} · SLOW {data.summary.slow} · FAIL {data.summary.fail} · UNKNOWN {data.summary.unknown}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {data.products.map((p) => {
          const cfg = statusConfig[p.status];
          return (
            <article
              key={p.slug}
              style={{
                padding: 16,
                background: "white",
                border: "1px solid #e1e6f0",
                borderRadius: 12,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 600 }}>{p.name}</h3>
                  <code style={{ fontSize: 11, color: "#5a6378" }}>{p.slug}</code>
                </div>
                <span
                  style={{
                    padding: "4px 10px",
                    background: cfg.bg,
                    color: cfg.color,
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {cfg.icon} {cfg.label}
                </span>
              </header>

              <dl
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "4px 12px",
                  margin: 0,
                  fontSize: 13,
                }}
              >
                <dt style={{ color: "#5a6378" }}>HTTP</dt>
                <dd style={{ margin: 0, fontFamily: "monospace" }}>{p.http_code ?? "—"}</dd>
                <dt style={{ color: "#5a6378" }}>TTFB</dt>
                <dd style={{ margin: 0, fontFamily: "monospace" }}>
                  {p.ttfb_ms != null ? `${p.ttfb_ms}ms` : "—"}
                </dd>
                <dt style={{ color: "#5a6378" }}>Total</dt>
                <dd style={{ margin: 0, fontFamily: "monospace" }}>
                  {p.total_ms != null ? `${p.total_ms}ms` : "—"}
                </dd>
                <dt style={{ color: "#5a6378" }}>Check</dt>
                <dd style={{ margin: 0 }}>{formatRelativeTime(p.last_check_at)}</dd>
              </dl>

              <footer style={{ borderTop: "1px solid #e1e6f0", paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: "#5a6378", marginBottom: 4 }}>Последний deploy</div>
                {p.last_deploy_at ? (
                  <>
                    <code style={{ fontSize: 11 }}>{p.last_deploy_sha}</code>
                    <span style={{ marginLeft: 8, fontSize: 12, color: "#5a6378" }}>
                      {formatRelativeTime(p.last_deploy_at)}
                    </span>
                    <div
                      style={{
                        fontSize: 12,
                        marginTop: 4,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={p.last_deploy_subject ?? ""}
                    >
                      {p.last_deploy_subject ?? "—"}
                    </div>
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: "#8693b0" }}>Нет данных</span>
                )}
              </footer>
            </article>
          );
        })}
      </div>

      <p style={{ marginTop: 32, fontSize: 12, color: "#8693b0", textAlign: "center" }}>
        Источник данных: <code>infra/scripts/synthetic-check.sh</code> (cron @ 15-min) +{" "}
        <code>git log apps/&lt;slug&gt;/</code> · Last update: {new Date(data.timestamp).toLocaleTimeString("ru")}
      </p>
      </div>
    </section>
  );
}
